import React, { useState, useEffect } from 'react';
import { PricingTier } from '../../types';

interface PricingTierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (pricingTiers: PricingTier[]) => void;
  initialPricingTiers?: PricingTier[];
  utilityName?: string;
  utilityType?: 'FIXED_SLOTS' | 'TIME_BASED';
}

const PricingTierModal: React.FC<PricingTierModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialPricingTiers = [],
  utilityName = '',
  utilityType = 'FIXED_SLOTS'
}) => {
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
  
  // Get terminology based on utility type
  const unitText = utilityType === 'FIXED_SLOTS' ? 'người/lượt' : 'giờ';
  const unitTextPlural = utilityType === 'FIXED_SLOTS' ? 'người/lượt' : 'giờ';

  useEffect(() => {
    if (isOpen) {
      if (initialPricingTiers.length > 0) {
        setPricingTiers([...initialPricingTiers]);
      } else {
        // Set default pricing tier
        setPricingTiers([{
          slotThreshold: 1,
          pricePerSlot: 50000,
          description: 'Giá mặc định'
        }]);
      }
    }
  }, [isOpen, initialPricingTiers]);

  const addPricingTier = () => {
    const newTier: PricingTier = {
      slotThreshold: Math.max(...pricingTiers.map(t => t.slotThreshold)) + 1,
      pricePerSlot: 40000,
      description: ''
    };
    setPricingTiers([...pricingTiers, newTier]);
  };

  const removePricingTier = (index: number) => {
    if (pricingTiers.length > 1) {
      const newTiers = pricingTiers.filter((_, i) => i !== index);
      setPricingTiers(newTiers);
    }
  };

  const updatePricingTier = (index: number, field: keyof PricingTier, value: string | number) => {
    const newTiers = [...pricingTiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setPricingTiers(newTiers);
  };

  const handleSave = () => {
    // Validate pricing tiers
    const validTiers = pricingTiers.filter(tier => 
      tier.slotThreshold > 0 && tier.pricePerSlot >= 0
    );

    if (validTiers.length === 0) {
      alert('Vui lòng thêm ít nhất một định mức giá hợp lệ');
      return;
    }

    // Check for duplicate thresholds
    const thresholds = validTiers.map(t => t.slotThreshold);
    const uniqueThresholds = new Set(thresholds);
    if (thresholds.length !== uniqueThresholds.size) {
      alert('Các định mức không được trùng lặp');
      return;
    }

    // Sort by threshold ascending
    const sortedTiers = validTiers.sort((a, b) => a.slotThreshold - b.slotThreshold);
    onSave(sortedTiers);
    onClose();
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(value);
  };

  const calculatePreviewCost = (slots: number) => {
    if (pricingTiers.length === 0) return 0;
    
    // Sort tiers by threshold descending to find applicable tier
    const sortedTiers = [...pricingTiers].sort((a, b) => b.slotThreshold - a.slotThreshold);
    
    let applicableTier = sortedTiers.find(tier => slots >= tier.slotThreshold);
    if (!applicableTier) {
      applicableTier = sortedTiers[sortedTiers.length - 1];
    }
    
    return slots * applicableTier.pricePerSlot;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block w-full max-w-4xl px-6 py-4 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Thiết lập định mức giá theo {unitTextPlural}{utilityName ? ` - ${utilityName}` : ''}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            {/* Pricing Tiers Configuration */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-medium text-gray-900">Định mức giá theo số {unitTextPlural}</h4>
                <button
                  onClick={addPricingTier}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  + Thêm định mức
                </button>
              </div>

              <div className="space-y-3">
                {pricingTiers.map((tier, index) => (
                  <div key={index} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Số {unitText} từ
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={tier.slotThreshold}
                        onChange={(e) => updatePricingTier(index, 'slotThreshold', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Giá 1 {unitText} (VND)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1000"
                        value={tier.pricePerSlot}
                        onChange={(e) => updatePricingTier(index, 'pricePerSlot', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    <div className="flex-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ghi chú
                      </label>
                      <input
                        type="text"
                        value={tier.description || ''}
                        onChange={(e) => updatePricingTier(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Ví dụ: Giá ưu đãi"
                      />
                    </div>

                    {pricingTiers.length > 1 && (
                      <button
                        onClick={() => removePricingTier(index)}
                        className="px-3 py-2 text-sm text-red-600 hover:text-red-800"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing Preview */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-medium text-gray-900 mb-3">Xem trước giá theo số {unitTextPlural}</h4>
              <div className="grid grid-cols-5 gap-4">
                {[1, 2, 3, 4, 5].map(slots => (
                                      <div key={slots} className="text-center p-3 bg-white rounded border">
                      <div className="text-sm font-medium text-gray-600">{slots} {unitText}</div>
                      <div className="text-lg font-bold text-blue-600">
                        {formatCurrency(calculatePreviewCost(slots))}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatCurrency(calculatePreviewCost(slots) / slots)}/{unitText}
                      </div>
                    </div>
                ))}
              </div>
            </div>

            {/* Example Explanation */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 mb-2">💡 Cách hoạt động:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• {unitText === 'người/lượt' ? 'Người/lượt' : 'Giờ'} đầu tiên (1 {unitText}): Áp dụng giá định mức đầu tiên</li>
                <li>• Khi đặt nhiều {unitText} hơn: Hệ thống sẽ tự động áp dụng giá ưu đãi tương ứng</li>
                <li>• Ví dụ: Đặt 1 {unitText} = 50,000 VND/{unitText}, đặt 4 {unitText} = 40,000 VND/{unitText}</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end mt-6 space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Hủy
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
            >
              Lưu định mức giá
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingTierModal; 